<template>
  <div
    :style="{ minWidth: '18vw', maxWidth: '18vw', minHeight: '180px', maxHeight: '180px' }"
    :class="[`shadow-lg w-full  z-10 max-h-screen mr-6 bg-white dark:bg-dark-screen-background rounded-lg relative`]"
    @mouseenter="handlePhaseSummaryEnter(phase)"
    @mouseleave="handlePhaseSummaryLeave">
    <div class="pb-6 p-2">
      <div :class="{ 'has-tooltip': $utils.textOverflows($refs, `phaseSummaryWrapper${phase.id}`) }" class="flex items-center mt-2">
        <span
          class="inline-flex mt-12 whitespace-normal ml-2 z-50 items-center gap-2 px-2 py-1 text-md font-medium rounded-md tooltip bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
          {{ phase.name }}</span>

        <input
          type="checkbox"
          :class="[`form-checkbox w-4 relative z-10  rounded h-4 dark:bg-dark-screen-background  text-${phase.color} `]"
          @input="(e) => toggleSelectedAllRecords(phase.results ? phase.results : phase.records.results, e.target.checked)">
        <div
          :ref="`phaseSummaryWrapper${phase.id}`"
          class="ml-3 text-grey-light-body dark:text-dark-body-text text-xl w-[55%] text-ellipsis whitespace-nowrap overflow-hidden">
          <span class="z-10 relative font-medium">{{ phase.name }}</span>
        </div>
        <div class="ml-auto relative z-10 dark:text-dark-body-text mr-2 text-2xl items-center flex">
          {{ phase.records_count }}
        </div>
        <div v-if="!phase.records_count && phase.records_count !== 0" class="italic z-10 text-common-grey mt-20 absolute">
          {{ $t("Loading records...") }}
        </div>
        <div
          :class="[
            `${
              phase.records_count_change
                ? phase.records_count_change > 0
                  ? 'text-green-success'
                  : 'text-red-error'
                : 'text-status-warning-yellow'
            } ${
              !phase.records_count && phase.records_count !== 0
                ? 'bg-gray-200'
                : phase.records_count_change
                  ? phase.records_count_change > 0
                    ? 'bg-green-success/10'
                    : 'bg-red-error/10'
                  : 'bg-status-warning-yellow/10'
            } px-2 relative z-10 rounded-full flex items-center`
          ]">
          <ChevronUp
            v-if="phase.records_count_change"
            :color="phase.records_count_change < 0 ? '#F05B5B' : '#27C884'"
            :class="[`${phase.records_count_change < 0 ? 'rotate-180' : ''} h-3 w-3 mr-1`]" />{{ phase.records_count_change }}
        </div>

        <div class="w-[105%] -ml-[2.5%] h-[330px] absolute z-0 cursor-move handle" />

        <Menu v-if="!editing_disabled" as="div" class="flex-shrink-0 relative left-1">
          <MenuButton
            class="w-5 h-5 inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
            <span class="sr-only">Open options</span>
            <EllipsisVertical class="w-5 h-5" aria-hidden="true" />
          </MenuButton>
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95">
            <MenuItems
              class="absolute z-50 mx-3 -ml-44 origin-bottom-right dark:bg-dark-screen-background w-48 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    ' cursor-pointer group flex items-center px-4 py-2 text-sm'
                  ]"
                  @click="handleEditPhaseClick(phase)">
                  <Pencil class="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                  {{ $t("Edit") }}
                </div>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    ' cursor-pointer group flex items-center px-4 py-2 text-sm'
                  ]"
                  @click="duplicatePhase(phase)">
                  <Copy class="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                  {{ $t("Duplicate") }}
                </div>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <div
                  :class="[
                    active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                    ' cursor-pointer group flex items-center px-4 py-2 text-sm'
                  ]"
                  @click="requestRemovePhase(phase, selectedJourney)">
                  <Trash2 class="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
                  {{ $t("Delete") }}
                </div>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
      <div class="mt-4 leading-tight pb-4">
        <div
          v-if="$store.state.data.solid_metrics_phases[phase.id]?.filter((stat) => stat.name == `Actual value`).length"
          class="flex my-2">
          <div class="text-common-grey relative z-10">
            {{ $t("Total Actual Value") }}
          </div>
          <div class="ml-auto text-grey-light-body dark:text-dark-body-text relative z-10">
            {{ phase.solid_stats.total_actual_value ? `$${$utils.truncateNumber(phase.solid_stats.total_actual_value)}` : "-" }}
          </div>
        </div>
        <div
          v-if="$store.state.data.solid_metrics_phases[phase.id]?.filter((stat) => stat.name == `Forecasted value`).length"
          class="flex my-2">
          <div class="text-common-grey relative z-10">
            {{ $t("Total Forecasted Value") }}
          </div>
          <div class="ml-auto text-grey-light-body dark:text-dark-body-text relative z-10">
            {{ phase.solid_stats.total_forecasted_value ? `$${$utils.truncateNumber(phase.solid_stats.total_forecasted_value)}` : "-" }}
          </div>
        </div>
        <div v-for="stats in phase.stats" :key="stats.id" class="flex my-2">
          <div class="text-common-grey w-[80%] text-ellipsis whitespace-nowrap overflow-hidden relative z-10">
            {{ stats.name }}
          </div>
          <div class="ml-auto relative z-10 text-grey-light-body dark:text-dark-body-text">
            {{ $utils.isNotEmpty(stats.value) ? $utils.humanizeMetricValue(stats, stats.value) : " - " }}
          </div>
        </div>
      </div>
      <div
        :class="[
          `w-[95%] border-b-2 ${
            !phase.records_count && phase.records_count !== 0
              ? 'border-gray-200'
              : phase.records_count_change
                ? phase.records_count_change > 0
                  ? 'border-green-success'
                  : 'border-red-error'
                : 'border-status-warning-yellow'
          } absolute bottom-5`
        ]" />
    </div>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import { ChevronUp, Trash2, EllipsisVertical, Pencil, Copy } from "lucide-vue-next"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
export default {
  components: {
    ChevronUp,
    EllipsisVertical,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Trash2,
    Pencil,
    Copy
  },

  inject: ["canUserEditLayout"],
  props: {
    phase: {
      type: Object,
      required: true
    },
    selectedJourney: {
      type: Object,
      required: true
    },
    journeys: {
      type: Array,
      required: true
    },
    toggleSelectedAllRecords: {
      type: Function,
      required: true
    }
  },
  emits: [],
  data() {
    return {
      phase_summary_hover_id: null
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  watch: {},

  created() {},

  methods: {
    handlePhaseSummaryEnter(phase) {
      this.$utils.debounce(() => {
        this.phase_summary_hover_id = phase.id
      }, 600)
    },
    handlePhaseSummaryLeave() {
      this.phase_summary_hover_id = null
    },
    handleEditPhaseClick(phase) {
      this.$emitter.emit(ALL_EMITTERS.EDIT_PHASE, { journeys: this.journeys, phase, selected_journey: this.selectedJourney })
    },
    duplicatePhase(phase) {
      dataService.duplicatePhase(phase.id).then((res) => {
        this.$store.dispatch("data/updatePhasesSolidMetrics", {
          id: res.data.id,
          list: []
        })
        this.$emitter.emit(ALL_EMITTERS.PHASES_ADDED_TO_JOURNEYS, res.data)
        //this.$emitter.emit(ALL_EMITTERS.PHASES_ADDED_TO_CUSTOMER_JOURNEYS_VIEW, res.data)
      })
    },
    requestRemovePhase(phase) {
      this.$emitter.emit(ALL_EMITTERS.DELETE_PHASE, { phase, selected_journey: this.selectedJourney })
    }
  }
}
</script>
